import React, { useMemo } from 'react';
import { CircleUser } from 'lucide-react';
import { ResumePreviewProps, WorkExperience, Education, Certification, Project } from '../types';
import { renderSocialLink } from '../utils/socialLinkRenderer';
import { S3_BASE_URL } from '../../../../config';

const formatDateWithFormat = (dateString: string | null, dateFormat?: string): string => {
  if (!dateString) return '';

  const date = /^\d{4}-\d{2}$/.test(dateString) ? new Date(`${dateString}-01`) : new Date(dateString);
  if (isNaN(date.getTime())) return "";

  if (!dateFormat) {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} '${year.toString().slice(-2)}`;
  }

  const month = date.getMonth() + 1;
  const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
  const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  const yearShort = year.toString().slice(-2);

  switch (dateFormat) {
    case '%b %y':
      return `${monthShort} ${yearShort}`;
    case '%B %Y':
      return `${monthLong} ${year}`;
    case '%b %Y':
      return `${monthShort} ${year}`;
    case '%m/%Y':
      return `${month.toString().padStart(2, '0')}/${year}`;
    case '%m/%y':
      return `${month.toString().padStart(2, '0')}/${yearShort}`;
    case '%Y':
      return year.toString();
    default:
      return `${monthShort} '${yearShort}`;
  }
};

const formatDateRange = (startDate: string | null, endDate: string | null, isCurrent: boolean, dateFormat?: string): string => {
  if (!startDate) return '';

  const start = formatDateWithFormat(startDate, dateFormat);
  if (isCurrent || !endDate) {
    return `${start} - Present`;
  }
  const end = formatDateWithFormat(endDate, dateFormat);
  return `${start} - ${end}`;
};

interface EditableFieldProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;

  return (
    <span
      className={`resume-editable-field ${isActive ? 'resume-editable-field--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </span>
  );
};

interface EditableBlockProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableBlock: React.FC<EditableBlockProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;

  return (
    <div
      className={`resume-editable-block ${isActive ? 'resume-editable-block--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </div>
  );
};

interface EditableLineProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableLine: React.FC<EditableLineProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;

  return (
    <li
      className={`resume-editable-line ${isActive ? 'resume-editable-line--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </li>
  );
};

export const ClassicTemplate: React.FC<ResumePreviewProps> = ({ data, settings, activeField, onFieldClick, sectionOrder, structures = [] }) => {
  const {
    personalInfo,
    targetJobTitle,
    professionalSummary,
    workExperiences,
    volunteerExperiences,
    educations,
    certifications,
    projects,
    skills,
    categorySkills,
    customSections,
    links
  } = data;

  const getFontFamily = (fontType?: string) => {
    if (!fontType) return undefined;
    const fontMap: Record<string, string> = {
      'Arial': 'Arial, sans-serif',
      'Bitter': '"Bitter", serif',
      'BreeSerif': '"Bree Serif", serif',
      'Carlito': '"Carlito", sans-serif',
      'EB Garamond': '"EB Garamond", serif',
      'Inter': '"Inter", sans-serif',
      'Lora': '"Lora", serif',
      'Noto Serif': '"Noto Serif", serif',
      'Roboto': '"Roboto", sans-serif',
      'Source Sans': '"Source Sans Pro", sans-serif',
      'Neuton': '"Neuton", serif',
    };
    return fontMap[fontType] || undefined;
  };

  const getLineHeight = (lineHeight?: string) => {
    if (!lineHeight) return undefined;
    const lineHeightMap: Record<string, string> = {
      '1 Tight': '1',
      '1.15 Condensed': '1.15',
      '1.3 Standard': '1.3',
      '1.5 Relaxed': '1.5',
      '2 Spacious': '2',
    };
    return lineHeightMap[lineHeight] || undefined;
  };

  const previewStyle: React.CSSProperties = useMemo(() => ({
    fontSize: settings?.font_size ? `${settings.font_size}pt` : undefined,
    lineHeight: getLineHeight(settings?.line_height),
    fontFamily: getFontFamily(settings?.text_font),
  }), [settings?.font_size, settings?.line_height, settings?.text_font]);

  const headingFontFamily = useMemo(() => getFontFamily(settings?.heading_font), [settings?.heading_font]);
  const displayFontFamily = useMemo(() => getFontFamily(settings?.display_font), [settings?.display_font]);
  const dateFormat = settings?.date_format;

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
  };

  const sectionTitlesMap = useMemo(() => {
    const map: Record<string, string> = {};
    structures.forEach(s => {
      const sectionId = structureTypeToId[s.structure_type];
      if (sectionId) {
        map[sectionId] = s.structure_name.toUpperCase();
      }
    });
    return map;
  }, [structures]);

  const getSectionTitle = (sectionId: string, defaultTitle: string): string => {
    return sectionTitlesMap[sectionId] || defaultTitle;
  };

  const defaultOrder = ['about', 'experience', 'volunteer', 'education', 'certifications', 'projects', 'skills'];
  const order = sectionOrder || defaultOrder;

  return (
    <div className="resume-preview resume-preview--classic" style={previewStyle}>
      {settings?.is_headshot && (
        <div className="resume-preview__headshot">
          {settings.headshot_url ? (
            <img
              src={settings.headshot_url.startsWith('http') ? settings.headshot_url : `${S3_BASE_URL}${settings.headshot_url}`}
              alt="Headshot"
              crossOrigin="anonymous"
              className={`resume-preview__headshot-img ${settings.headshot_format === 'circle' ? 'resume-preview__headshot-img--circle' : 'resume-preview__headshot-img--square'}`}
            />
          ) : (
            <div className={`resume-preview__headshot-placeholder ${settings.headshot_format === 'circle' ? 'resume-preview__headshot-placeholder--circle' : 'resume-preview__headshot-placeholder--square'}`}>
              <CircleUser size={24} />
            </div>
          )}
        </div>
      )}
      <div className="resume-preview__header">
        <div style={{ flex: 1, paddingLeft: settings?.is_headshot ? '115px' : undefined, paddingRight: settings?.is_headshot ? '115px' : undefined }}>
          <h1 className="resume-preview__name" style={displayFontFamily ? { fontFamily: displayFontFamily } : undefined}>
          <EditableField fieldName="firstName" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.firstName}
          </EditableField>
          {' '}
          <EditableField fieldName="lastName" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.lastName}
          </EditableField>
          {targetJobTitle && (
            <>
              {', '}
              <EditableField fieldName="position" activeField={activeField} onFieldClick={onFieldClick}>
                {targetJobTitle}
              </EditableField>
            </>
          )}
        </h1>
        <p className="resume-preview__contact">
          <EditableField fieldName="email" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.email}
          </EditableField>
          {personalInfo.phone && (
            <>
              {' | '}
              <EditableField fieldName="phone" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.phone}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'linkedin') && (
            <>
              {' | '}
              <EditableField fieldName="linkedin" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('linkedin', links.find(l => l.type === 'linkedin')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'telegram') && (
            <>
              {' | '}
              <EditableField fieldName="telegram" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('telegram', links.find(l => l.type === 'telegram')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'whatsapp') && (
            <>
              {' | '}
              <EditableField fieldName="whatsapp" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('whatsapp', links.find(l => l.type === 'whatsapp')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'github') && (
            <>
              {' | '}
              <EditableField fieldName="github" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('github', links.find(l => l.type === 'github')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'twitter') && (
            <>
              {' | '}
              <EditableField fieldName="twitter" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('twitter', links.find(l => l.type === 'twitter')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'dribbble') && (
            <>
              {' | '}
              <EditableField fieldName="dribbble" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('dribbble', links.find(l => l.type === 'dribbble')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {personalInfo.location && (
            <>
              {' | '}
              <EditableField fieldName="location" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.location}
              </EditableField>
            </>
          )}
        </p>
        </div>
      </div>

      {order.map((sectionId) => {
        if (sectionId === 'about' && professionalSummary) {
          return (
            <div key="about" className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('about', 'Professional Summary')}</h2>
              <EditableBlock fieldName="summary" activeField={activeField} onFieldClick={onFieldClick} className="resume-preview__summary-block">
                <ul className="resume-preview__summary-list">
                  {professionalSummary.split('\n').filter(line => line.trim()).map((line, index) => (
                    <li key={index}>{line.replace(/^[•\-]\s*/, '')}</li>
                  ))}
                </ul>
              </EditableBlock>
            </div>
          );
        }

        if (sectionId === 'experience' && workExperiences && workExperiences.length > 0) {
          return (
            <div key="experience" className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('experience', 'Work Experience')}</h2>
              {workExperiences.map((experience: WorkExperience) => (
                <EditableBlock
                  key={experience.uid}
                  fieldName={`experience.${experience.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-preview__job"
                >
                  <div className="resume-preview__job-header">
                    <strong>
                      <EditableField
                        fieldName={`experience.${experience.uid}.employer_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {experience.employer_name}
                      </EditableField>
                    </strong>
                    <span className="resume-preview__date">
                      <EditableField
                        fieldName={`experience.${experience.uid}.start_date`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {formatDateRange(experience.start_date, experience.end_date, experience.is_current, dateFormat)}
                      </EditableField>
                    </span>
                  </div>
                  <div className="resume-preview__job-title">
                    <EditableField
                      fieldName={`experience.${experience.uid}.job_title`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {experience.job_title}
                    </EditableField>
                  </div>
                  {experience.description && (
                    <EditableField
                      fieldName={`experience.${experience.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-preview__job-description"
                    >
                      {experience.description}
                    </EditableField>
                  )}
                  {experience.achievements && experience.achievements.length > 0 && (
                    <ul className="resume-preview__list">
                      {experience.achievements.map((achievement, index) => (
                        <EditableLine
                          key={index}
                          fieldName={`experience.${experience.uid}.achievement.${index}`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {achievement}
                        </EditableLine>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              ))}
            </div>
          );
        }

        if (sectionId === 'volunteer' && volunteerExperiences && volunteerExperiences.length > 0) {
          return (
            <div key="volunteer" className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('volunteer', 'Volunteer Experience')}</h2>
              {volunteerExperiences.map((experience: WorkExperience) => (
                <EditableBlock
                  key={experience.uid}
                  fieldName={`experience.${experience.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-preview__job"
                >
                  <div className="resume-preview__job-header">
                    <strong>
                      <EditableField
                        fieldName={`experience.${experience.uid}.employer_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {experience.employer_name}
                      </EditableField>
                    </strong>
                    <span className="resume-preview__date">
                      <EditableField
                        fieldName={`experience.${experience.uid}.start_date`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {formatDateRange(experience.start_date, experience.end_date, experience.is_current, dateFormat)}
                      </EditableField>
                    </span>
                  </div>
                  <div className="resume-preview__job-title">
                    <EditableField
                      fieldName={`experience.${experience.uid}.job_title`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {experience.job_title}
                    </EditableField>
                  </div>
                  {experience.description && (
                    <EditableField
                      fieldName={`experience.${experience.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-preview__job-description"
                    >
                      {experience.description}
                    </EditableField>
                  )}
                  {experience.achievements && experience.achievements.length > 0 && (
                    <ul className="resume-preview__list">
                      {experience.achievements.map((achievement, index) => (
                        <EditableLine
                          key={index}
                          fieldName={`experience.${experience.uid}.achievement.${index}`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {achievement}
                        </EditableLine>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              ))}
            </div>
          );
        }

        if (sectionId === 'education' && educations && educations.length > 0) {
          return (
            <div key="education" className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('education', 'Education')}</h2>
              {educations.map((education: Education) => (
                <EditableBlock
                  key={education.uid}
                  fieldName={`education.${education.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-preview__education"
                >
                  <div className="resume-preview__education-header">
                    <strong>
                      <EditableField
                        fieldName={`education.${education.uid}.institution`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {education.institution}
                      </EditableField>
                    </strong>
                    {education.graduation_date && (
                      <span className="resume-preview__date">
                        <EditableField
                          fieldName={`education.${education.uid}.graduation_date`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {formatDateWithFormat(education.graduation_date, dateFormat)}
                        </EditableField>
                      </span>
                    )}
                  </div>
                  <div className="resume-preview__education-degree">
                    <EditableField
                      fieldName={`education.${education.uid}.degree`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {education.degree}
                    </EditableField>
                    {education.field_of_study && (
                      <>
                        {', '}
                        <EditableField
                          fieldName={`education.${education.uid}.field_of_study`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {education.field_of_study}
                        </EditableField>
                      </>
                    )}
                    {education.grade && (
                      <>
                        {' - '}
                        <EditableField
                          fieldName={`education.${education.uid}.grade`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {education.grade}
                        </EditableField>
                      </>
                    )}
                  </div>
                  {education.description && (
                    <EditableField
                      fieldName={`education.${education.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-preview__text"
                    >
                      {education.description}
                    </EditableField>
                  )}
                </EditableBlock>
              ))}
            </div>
          );
        }

        if (sectionId === 'certifications' && certifications && certifications.length > 0) {
          return (
            <div key="certifications" className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('certifications', 'Certifications')}</h2>
              {certifications.map((cert: Certification) => {
                const isStacked = settings?.layout === 'Stacked';
                return (
                  <EditableBlock
                    key={cert.uid}
                    fieldName={`certification.${cert.uid}`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-preview__certification"
                  >
                    <div className="resume-preview__certification-header">
                      <strong>
                        <EditableField
                          fieldName={`certification.${cert.uid}.certification_name`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {cert.certification_name}
                        </EditableField>
                      </strong>
                      {cert.issued_at && (
                        <span className="resume-preview__date">
                          <EditableField
                            fieldName={`certification.${cert.uid}.issued_at`}
                            activeField={activeField}
                            onFieldClick={onFieldClick}
                          >
                            {formatDateWithFormat(cert.issued_at, dateFormat)}
                            {cert.expires_at && ` - ${formatDateWithFormat(cert.expires_at, dateFormat)}`}
                          </EditableField>
                        </span>
                      )}
                      {!cert.issued_at && cert.expires_at && (
                        <span className="resume-preview__date">
                          <EditableField
                            fieldName={`certification.${cert.uid}.expires_at`}
                            activeField={activeField}
                            onFieldClick={onFieldClick}
                          >
                            Expires: {formatDateWithFormat(cert.expires_at, dateFormat)}
                          </EditableField>
                        </span>
                      )}
                    </div>
                    {cert.organization_name && (
                      <div className="resume-preview__certification-org">
                        <EditableField
                          fieldName={`certification.${cert.uid}.organization_name`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {cert.organization_name}
                        </EditableField>
                        {cert.credential_id && (
                          <>
                            {' | '}
                            <EditableField
                              fieldName={`certification.${cert.uid}.credential_id`}
                              activeField={activeField}
                              onFieldClick={onFieldClick}
                            >
                              {cert.credential_id}
                            </EditableField>
                          </>
                        )}
                      </div>
                    )}
                    {!cert.organization_name && cert.credential_id && (
                      <div className="resume-preview__certification-org">
                        <EditableField
                          fieldName={`certification.${cert.uid}.credential_id`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {cert.credential_id}
                        </EditableField>
                      </div>
                    )}
                    {cert.description && (
                      <EditableField
                        fieldName={`certification.${cert.uid}.description`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                        className="resume-preview__text"
                      >
                        {cert.description}
                      </EditableField>
                    )}
                  </EditableBlock>
                );
              })}
            </div>
          );
        }

        if (sectionId === 'projects' && projects && projects.length > 0) {
          return (
            <div key="projects" className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('projects', 'Projects')}</h2>
              {projects.map((project: Project) => (
                <EditableBlock
                  key={project.uid}
                  fieldName={`project.${project.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-preview__job"
                >
                  <div className="resume-preview__job-header">
                    <strong>
                      <EditableField
                        fieldName={`project.${project.uid}.project_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {project.project_name}
                      </EditableField>
                      {project.url && (
                        <>
                          {' | '}
                          <EditableField
                            fieldName={`project.${project.uid}.url`}
                            activeField={activeField}
                            onFieldClick={onFieldClick}
                          >
                            {project.url}
                          </EditableField>
                        </>
                      )}
                    </strong>
                    {project.start_date && (
                      <span className="resume-preview__date">
                        <EditableField
                          fieldName={`project.${project.uid}.start_date`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {formatDateRange(project.start_date, project.end_date || null, project.is_current || false, dateFormat)}
                        </EditableField>
                      </span>
                    )}
                  </div>
                  {project.description && (
                    <EditableField
                      fieldName={`project.${project.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-preview__job-description"
                    >
                      {project.description}
                    </EditableField>
                  )}
                  {project.achievements && project.achievements.length > 0 && (
                    <ul className="resume-preview__list">
                      {project.achievements.map((achievement, index) => (
                        <EditableLine
                          key={index}
                          fieldName={`project.${project.uid}.achievement.${index}`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {achievement}
                        </EditableLine>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              ))}
            </div>
          );
        }

        const hasSkillsInCategories = categorySkills && Object.values(categorySkills).some(skillsList => skillsList && skillsList.length > 0);
        if (sectionId === 'skills' && ((skills && skills.length > 0) || hasSkillsInCategories)) {
          const categoriesWithSkills = categorySkills ? Object.entries(categorySkills).filter(([_, skillsList]) => skillsList && skillsList.length > 0) : [];

          return (
            <div key="skills" className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('skills', 'Skills')}</h2>
              <ul className="resume-preview__list">
                {categoriesWithSkills.length > 0 ? (
                  categoriesWithSkills.map(([category, categorySkillsList]) => (
                    <li key={category}>
                      <EditableField
                        fieldName={`skills.category.${category}`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        <strong>{category}</strong>: {categorySkillsList.join(', ')}.
                      </EditableField>
                    </li>
                  ))
                ) : (
                  skills && skills.length > 0 && (
                    <li>
                      <EditableField
                        fieldName="skills"
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {skills.join(', ')}.
                      </EditableField>
                    </li>
                  )
                )}
              </ul>
            </div>
          );
        }

        const customSection = customSections?.find(cs => cs.id === sectionId);
        if (customSection) {
          return (
            <div key={customSection.id} className="resume-preview__section">
              <h2 className="resume-preview__title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{customSection.title.toUpperCase()}</h2>
              {customSection.layout === 'stacked' ? (
                customSection.entries.map((entry) => (
                  <div key={entry.id} className="resume-preview__job">
                    <div className="resume-preview__job-header">
                      <strong>{entry.title}</strong>
                      {entry.date && !entry.hideDate && (
                        <span className="resume-preview__date">
                          {entry.yearOnly
                            ? new Date(entry.date).getFullYear()
                            : formatDateWithFormat(entry.date, dateFormat)}
                          {entry.endDate && !entry.hideEndDate && (
                            <> - {entry.yearOnly
                              ? new Date(entry.endDate).getFullYear()
                              : formatDateWithFormat(entry.endDate, dateFormat)}</>
                          )}
                        </span>
                      )}
                    </div>
                    {entry.description && (
                      <p className="resume-preview__text">{entry.description}</p>
                    )}
                  </div>
                ))
              ) : (
                <ul className="resume-preview__list">
                  {customSection.entries.map((entry) => (
                    <li key={entry.id}>
                      <strong>{entry.title}</strong>
                      {entry.description && `: ${entry.description}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};
